import { useState } from 'react'
import {
  Boxes,
  Search,
  TrendingUp,
  Sparkles,
  Download,
  Users
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { FluentCard } from '@/components/layout/page-header'
import { PresetsTab } from './PresetsTab'

interface StatsCardProps {
  icon: React.ElementType
  label: string
  value: string | number
  trend?: string
  trendUp?: boolean
  color: string
}

function StatsCard({ icon: Icon, label, value, trend, trendUp, color }: StatsCardProps) {
  return (
    <FluentCard className="relative overflow-hidden">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trendUp ? 'text-emerald-600' : 'text-red-500'}`}>
              <TrendingUp className={`h-3 w-3 ${!trendUp && 'rotate-180'}`} />
              {trend}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </FluentCard>
  )
}

export default function AIPresetsPage() {
  const [searchQuery, setSearchQuery] = useState('')

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-none bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 lg:p-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-sm">
              <Boxes className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-1">Industry Presets</h1>
              <p className="text-white/80">Pre-configured AI agents tailored for Legal, Finance, Healthcare, Insurance, Pharma & more</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          icon={Boxes}
          label="Available Presets"
          value={22}
          color="bg-gradient-to-br from-amber-400 to-orange-600"
        />
        <StatsCard
          icon={Download}
          label="Sample Companies"
          value={7}
          color="bg-gradient-to-br from-emerald-400 to-green-600"
        />
        <StatsCard
          icon={Sparkles}
          label="Featured"
          value={14}
          color="bg-gradient-to-br from-violet-400 to-purple-600"
        />
        <StatsCard
          icon={Users}
          label="Industries"
          value={7}
          color="bg-gradient-to-br from-blue-400 to-cyan-600"
        />
      </div>

      {/* Main Content */}
      <FluentCard className="p-0">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Browse Presets</h2>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search presets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full lg:w-64 rounded-xl border-gray-200 dark:border-gray-700"
            />
          </div>
        </div>

        <div className="p-6">
          <PresetsTab searchQuery={searchQuery} />
        </div>
      </FluentCard>
    </div>
  )
}
